/**
 * Audio view component.
 */

import React from 'react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { addPrefixPath } from '@serpro/volto-prefixpath/helpers/Url/Url';
import DateSocialShareBody from 'govbr.frontend.policy/components/Blocks/DateShare/DateSocialShareBody';
import { formatFileSize } from 'govbr.frontend.policy/helpers/formatFileSize';
import { messages } from 'govbr.frontend.policy/messages/audio';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Container } from 'semantic-ui-react';

const AudioView = ({ content }) => {
  const audioFile = content.audio_file;
  const downloadUrl = audioFile?.download;
  const audioType =
    audioFile?.contentType ||
    audioFile?.content_type ||
    audioFile?.['content-type'];
  const fileLabel = audioFile?.filename || audioFile?.title || content.title;
  const fileSize = typeof audioFile?.size === 'number' ? audioFile.size : null;
  const intl = useIntl();
  const transcriptParagraphs = content.transcript
    ?.split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return (
    <Container className="view-wrapper mb-3xh">
      <h1 className="documentFirstHeading">{content.title}</h1>
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
      <DateSocialShareBody properties={content} hideTag />
      {downloadUrl && (
        <div className="mb-3x">
          {
            // Media elements such as <audio> and <video> must have a <track> for captions
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls className="w-100">
              <source src={addPrefixPath(downloadUrl)} type={audioType} />
              {intl.formatMessage(messages.audioUnsupported)}
            </audio>
          }
        </div>
      )}
      {transcriptParagraphs?.length > 0 && (
        <div className="mb-3x">
          <div className="label">
            {intl.formatMessage(messages.transcriptLabel)}
          </div>
          {transcriptParagraphs.map((paragraph, index) => (
            <p key={`transcript-paragraph-${index}`}>{paragraph}</p>
          ))}
        </div>
      )}
      {downloadUrl && (
        <div className="mb-3x">
          <div className="label">
            {intl.formatMessage(messages.audioDownloadLabel)}
          </div>
          <UniversalLink href={downloadUrl}>{fileLabel}</UniversalLink>
          {fileSize !== null && (
            <span className="text-down-01">
              &nbsp;&mdash;&nbsp;
              {formatFileSize(fileSize)}
            </span>
          )}
        </div>
      )}
    </Container>
  );
};

AudioView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    transcript: PropTypes.string,
    category: PropTypes.shape({
      nomeCategoria: PropTypes.string,
      categoriaSuperior: PropTypes.object,
    }),
    audio_file: PropTypes.shape({
      download: PropTypes.string,
      filename: PropTypes.string,
      size: PropTypes.number,
      contentType: PropTypes.string,
    }),
  }).isRequired,
};

export default AudioView;
